import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiArrowRight,
  FiCheckCircle,
  FiTrendingUp,
  FiZap,
  FiShield,
  FiHeart,
  FiGlobe
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './APM.css';

const Careers = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/careers', {
      page_title: 'Careers at LogCrest - Join Our Team',
      page_category: 'careers'
    });
  }, []);

  const benefits = [
    {
      icon: <FiHeart />,
      title: 'Health & Wellness',
      description: 'Comprehensive health, dental, and vision coverage for you and your family'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Career Growth',
      description: 'Professional development opportunities and clear career advancement paths'
    },
    {
      icon: <FiGlobe />,
      title: 'Remote First',
      description: 'Work from anywhere with flexible hours and home office stipend'
    },
    {
      icon: <FiZap />,
      title: 'Learning Budget',
      description: 'Annual budget for conferences, courses, and professional development'
    },
    {
      icon: <FiShield />,
      title: 'Equity & Retirement',
      description: 'Competitive equity packages and 401(k) matching program'
    },
    {
      icon: <FiUsers />,
      title: 'Team Events',
      description: 'Regular team building events and company retreats'
    }
  ];

  const values = [
    'Innovation and continuous learning',
    'Customer-first mindset',
    'Collaboration and transparency',
    'Diversity and inclusion',
    'Work-life balance',
    'Impact-driven results'
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Join the LogCrest Team</h1>
            <p className="hero-subtitle">
              Help us build the future of intelligent observability. We're looking for 
              passionate individuals who want to make a real impact on how organizations 
              monitor and optimize their infrastructure.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'block', fontSize: '2.5rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem' }}>50+</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Team Members</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'block', fontSize: '2.5rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem' }}>15+</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Countries</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'block', fontSize: '2.5rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem' }}>100%</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Remote Friendly</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Why Join LogCrest?</h2>
            <p>Be part of a mission-driven team building the next generation of observability tools</p>
          </motion.div>
          
          <div className="features-grid">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {benefit.icon}
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Our Culture</h2>
              <p>
                At LogCrest, we believe that great products are built by great teams. 
                We foster a culture of innovation, collaboration, and continuous learning 
                where everyone has the opportunity to make a meaningful impact.
              </p>
              <p>
                We're committed to building a diverse and inclusive workplace where 
                different perspectives are valued and everyone can thrive.
              </p>
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-primary)' }}>Our Values</h3>
                <ul className="benefits-list">
                  {values.map((value, index) => (
                    <motion.li 
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <FiCheckCircle />
                      {value}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Team Collaboration</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiUsers />
                    <span>50+ Team Members</span>
                  </div>
                  <div className="metric">
                    <FiGlobe />
                    <span>15+ Countries</span>
                  </div>
                  <div className="metric">
                    <FiHeart />
                    <span>100% Remote</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Don't see the right role?</h2>
            <p>
              We're always looking for exceptional talent. If you're passionate about 
              observability, AI, and building great products, we'd love to hear from you.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Get in Touch
                <FiArrowRight />
              </Link>
              <a href="mailto:careers@logs-rider.com" className="btn btn-outline">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
